import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import _ from "lodash";

import useMetadataStore from "@/state/metadata";
import withWidgetChildrenLoader from "@/hocs/WidgetContainer/withWidgetChildrenLoader";

import "@inovua/reactdatagrid-community/index.css";

import useDashboardStore from "@/state/dashboard";
import {
  DataTable,
  DataTableCell,
  DataTableColumnHeader,
  DataTableRow,
  Pagination,
  TableBody,
  TableHead,
} from "@dhis2/ui";
import { convertDataByDataType, pickTranslation } from "../../utils";
import { INIT_MASTER_FACILITIES_LIST_STATE } from "..";
import MaterFacilityDetail from "../components/DetailsV2";

const Widget1 = ({ setLoading }) => {
  const { t, i18n } = useTranslation();

  const { program, orgUnits, orgUnitGroups } = useMetadataStore(
    (state) => ({
      program: state.oupProgram,
      orgUnits: state.oupOrgUnits,
      orgUnitGroups: state.oupOrgUnitGroups,
    }),
    shallow
  );
  const { changeAdditionalStateProperty, additionalState } = useDashboardStore(
    (state) => ({
      changeAdditionalStateProperty: state.changeAdditionalStateProperty,
      additionalState: state.additionalState,
    }),
    shallow
  );
  const [selectedFacility, setSelectedFacility] = useState({});

  useEffect(() => {
    setLoading(!additionalState.dashboard8Ready);
  }, [additionalState.dashboard8Ready]);

  if (!additionalState.dashboard8Data) return null;
  const { masterFacilitiesListState = INIT_MASTER_FACILITIES_LIST_STATE } =
    additionalState;
  const {
    columns,
    paging: { page, pageSize },
  } = masterFacilitiesListState;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const data = additionalState.filteredData || additionalState.dashboard8Data;

  const currentData = data.slice(startIndex, endIndex);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {currentData.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            overflow: "auto",
            height: "calc(100% - 50px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p sx={{ fontWeight: "bold" }}>{t("emptyData")}</p>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            overflow: "auto",
            height: "calc(100% - 50px)",
            "& tbody tr": {
              cursor: "pointer",
            },
            "& thead tr": {
              position: "sticky",
              top: 0,
            },
          }}
        >
          <DataTable>
            <TableHead>
              <DataTableRow>
                {columns.map((column, index) => {
                  const foundAttribute = program.trackedEntityAttributes.find(
                    (tea) => tea.id === column
                  );
                  return (
                    <DataTableColumnHeader key={column}>
                      {foundAttribute
                        ? pickTranslation(
                            foundAttribute,
                            i18n.language,
                            "formName"
                          )
                        : t(column)}
                    </DataTableColumnHeader>
                  );
                })}
              </DataTableRow>
            </TableHead>
            <TableBody>
              {currentData.map((facility, facilityIndex) => (
                <DataTableRow
                  onClick={() => {
                    setSelectedFacility(facility);
                  }}
                  key={facilityIndex}
                >
                  {columns.map((column, columnIndex) => (
                    <DataTableCell key={columnIndex}>
                      {convertDataByDataType(facility[column], column, {
                        ...program,
                        orgUnits,
                      })}
                    </DataTableCell>
                  ))}
                </DataTableRow>
              ))}
            </TableBody>
          </DataTable>
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          height: "50px",
          borderTop: "1px solid #E8EDF2",
          display: "flex",
          alignItems: "center",
          padding: 3,
        }}
      >
        <Pagination
          page={page}
          pageCount={Math.ceil(data.length / pageSize)}
          pageSize={pageSize}
          total={data.length}
          hidePageSizeSelect
          onPageChange={(value) => {
            const newMasterFacilitiesListState = {
              ...masterFacilitiesListState,
              paging: {
                ...masterFacilitiesListState.paging,
                page: value,
              },
            };
            changeAdditionalStateProperty(
              "masterFacilitiesListState",
              newMasterFacilitiesListState
            );
          }}
        />
      </Box>
      <MaterFacilityDetail
        facility={selectedFacility}
        onClose={() => setSelectedFacility({})}
      />
    </Box>
  );
};

export default withWidgetChildrenLoader(Widget1);
